import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LogoMark } from "../ui/Logo";
import { LanguageMenu } from "../ui/LanguageSwitcher";

// Landing-page sections, in page order — the scroll-spy relies on that order.
const SECTION_IDS = ["services", "portfolio", "pricing", "contact"];

// The fixed header is h-20; index.css clears it with the same scroll-padding-top.
const HEADER_HEIGHT = 80;

// How long the page must be still before a click-started smooth scroll counts
// as finished and the scroll-spy takes over again.
const SCROLL_SETTLE_MS = 150;

// Two complete class sets rather than one constant plus an appended colour: a
// colliding utility appended to a shared constant loses on stylesheet order.
const navLinkBase =
  "nav-link scrollto relative inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-medium transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600";
const navLinkIdle = `${navLinkBase} text-ink-600 hover:bg-brand-50 hover:text-brand-700`;
const navLinkActive = `${navLinkBase} active bg-brand-100 text-brand-800`;

const mobileLinkBase =
  "flex items-center justify-between border-b border-ink-100 py-3.5 text-base transition-colors";
const mobileLinkIdle = `${mobileLinkBase} font-medium text-ink-700 hover:text-brand-700`;
const mobileLinkActive = `${mobileLinkBase} active font-semibold text-brand-700`;

/**
 * Site header / primary navigation.
 *
 * Load-bearing for the e2e suite: `#header`, `#navbar`, the `.dropdown`
 * wrapper around the `.bi-globe` language selector, and the exact link
 * label "Login".
 */
function Menu({ authenticated, onLogout }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  // While a click-started smooth scroll is in flight, the spy would light up
  // every section it passes on the way. The clicked one is held instead.
  const spyLockRef = useRef(false);
  const settleTimerRef = useRef(0);

  const isHome = location.pathname === "/";
  const discoverActive = location.pathname.startsWith("/discover");

  // Don't leave the menu open behind a route change.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  // Scroll-spy: the active section is the last one whose top has crossed a line
  // a third of the way down the visible area below the header.
  useEffect(() => {
    let frame = 0;

    const update = () => {
      frame = 0;
      setScrolled(window.scrollY > 8);

      if (spyLockRef.current) return;
      if (!isHome) {
        setActiveSection(null);
        return;
      }

      const probe =
        HEADER_HEIGHT + (window.innerHeight - HEADER_HEIGHT) * 0.35;
      let current = null;
      for (const id of SECTION_IDS) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= probe) current = id;
      }

      // Scrolled to the very bottom: the last section may be too short to ever
      // reach the probe line, yet it is plainly the one being read.
      const atBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 2;
      const last = SECTION_IDS[SECTION_IDS.length - 1];
      if (atBottom && document.getElementById(last)) current = last;

      setActiveSection(current);
    };

    const onScroll = () => {
      if (spyLockRef.current) {
        clearTimeout(settleTimerRef.current);
        settleTimerRef.current = setTimeout(releaseSpy, SCROLL_SETTLE_MS);
      }
      if (!frame) frame = requestAnimationFrame(update);
    };

    const releaseSpy = () => {
      spyLockRef.current = false;
      update();
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [isHome]);

  useEffect(() => () => clearTimeout(settleTimerRef.current), []);

  // An open full-screen menu shouldn't scroll the page behind it.
  useEffect(() => {
    document.body.style.overflow = mobileNavOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileNavOpen]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setMobileNavOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const closeMobileNav = () => setMobileNavOpen(false);

  const handleNavClick = (e, sectionId) => {
    e.preventDefault();
    closeMobileNav();

    // Green straight away, and held until the scroll there has settled.
    setActiveSection(sectionId);
    spyLockRef.current = true;
    clearTimeout(settleTimerRef.current);
    settleTimerRef.current = setTimeout(() => {
      spyLockRef.current = false;
    }, SCROLL_SETTLE_MS * 4);

    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        document
          .getElementById(sectionId)
          ?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    closeMobileNav();
    if (location.pathname !== "/") {
      navigate("/");
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const sections = [
    { id: "services", label: t("Menu.Services") },
    { id: "portfolio", label: t("Menu.Portfolio") },
    { id: "pricing", label: t("Menu.Pricing") },
    { id: "contact", label: t("Menu.Contact") },
  ];

  const ctaClass =
    "getstarted scrollto inline-flex items-center rounded-full bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white shadow-brand transition hover:-translate-y-px hover:bg-brand-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 motion-reduce:hover:translate-y-0";

  return (
    <header
      id="header"
      className={`fixed inset-x-0 top-0 z-50 h-20 border-b backdrop-blur-md transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled
          ? "border-ink-200 bg-white/95 shadow-card"
          : "border-ink-200/70 bg-white/85"
      }`}
    >
      <div className="mx-auto flex h-full max-w-6xl items-center justify-between px-4 sm:px-6">
        {/* Logo */}
        <h1 className="logo m-0 text-xl font-bold tracking-tight sm:text-2xl">
          <a
            href="/"
            onClick={handleLogoClick}
            className="flex items-center gap-2 text-ink-900"
          >
            <LogoMark className="size-8 shrink-0 text-brand-600" />
            {/* One element, so the wordmark is a single flex item. Left bare,
                "Easy" becomes an anonymous flex item and the parent's gap-2
                opens a space in the middle of the name. */}
            <span>
              Easy<span className="text-brand-600">Planner</span>
            </span>
          </a>
        </h1>

        <nav id="navbar" className="navbar flex items-center gap-1.5 lg:gap-3">
          {/* Desktop nav */}
          <ul className="hidden items-center gap-1 lg:flex">
            {/* First, and a route rather than an anchor: finding a business is
                what a visitor came to do, and it must stay reachable from every
                page rather than only by scrolling the landing page. */}
            <li>
              <Link
                className={discoverActive ? navLinkActive : navLinkIdle}
                to="/discover"
                aria-current={discoverActive ? "page" : undefined}
              >
                <i className="bx bx-search-alt text-base" aria-hidden="true" />
                {t("Menu.Discover")}
              </Link>
            </li>

            {sections.map((s) => {
              const active = activeSection === s.id;
              return (
                <li key={s.id}>
                  <a
                    className={active ? navLinkActive : navLinkIdle}
                    href={`#${s.id}`}
                    onClick={(e) => handleNavClick(e, s.id)}
                    aria-current={active ? "location" : undefined}
                  >
                    {s.label}
                  </a>
                </li>
              );
            })}

            {/* One way in: a new visitor reaches signup from the login page's
                "Create an account" link, so the header doesn't offer both. */}
            {!authenticated ? (
              <li className="ml-3">
                <Link className={ctaClass} to="/login">
                  {t("Menu.LogIn")}
                </Link>
              </li>
            ) : (
              <li className="ml-3">
                <button type="button" className={ctaClass} onClick={onLogout}>
                  {t("Menu.Logout")}
                </button>
              </li>
            )}
          </ul>

          {/* Language — outside the drawer, so it is one tap on a phone too:
              after Login on desktop, beside the menu toggle on mobile. */}
          <LanguageMenu triggerClassName="h-11 rounded-full px-3 text-sm font-semibold text-ink-700 hover:bg-ink-100 hover:text-brand-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 lg:h-10" />

          {/* Mobile toggle — 44px touch target */}
          <button
            type="button"
            onClick={() => setMobileNavOpen((v) => !v)}
            aria-expanded={mobileNavOpen}
            aria-controls="mobile-menu"
            aria-label={mobileNavOpen ? t("Common.closeMenu") : t("Common.openMenu")}
            className="mobile-nav-toggle -mr-2 inline-flex size-11 items-center justify-center rounded-lg text-ink-700 transition-colors hover:bg-ink-100 lg:hidden"
          >
            <i
              className={`bi ${mobileNavOpen ? "bi-x" : "bi-list"} text-2xl`}
              aria-hidden="true"
            />
          </button>
        </nav>
      </div>

      {/* Mobile drawer */}
      {mobileNavOpen && (
        <div className="lg:hidden">
          <div
            className="fixed inset-0 top-20 bg-ink-900/40"
            onClick={closeMobileNav}
            aria-hidden="true"
          />
          <div
            id="mobile-menu"
            className="fixed inset-x-0 top-20 max-h-[calc(100vh-5rem)] overflow-y-auto border-b border-ink-200 bg-white shadow-card-hover"
          >
            <ul className="px-4 py-3">
              <li>
                <Link
                  className="flex items-center gap-2 border-b border-ink-100 py-3.5 text-base font-semibold text-brand-700"
                  to="/discover"
                  onClick={closeMobileNav}
                >
                  <i className="bx bx-search-alt text-lg" aria-hidden="true" />
                  {t("Menu.Discover")}
                </Link>
              </li>

              {sections.map((s) => {
                const active = activeSection === s.id;
                return (
                  <li key={s.id}>
                    <a
                      className={active ? mobileLinkActive : mobileLinkIdle}
                      href={`#${s.id}`}
                      onClick={(e) => handleNavClick(e, s.id)}
                      aria-current={active ? "location" : undefined}
                    >
                      {s.label}
                      {active && (
                        <span
                          className="size-2 rounded-full bg-brand-600"
                          aria-hidden="true"
                        />
                      )}
                    </a>
                  </li>
                );
              })}

              {!authenticated ? (
                <li className="pb-2 pt-3">
                  <Link
                    className="getstarted block rounded-full bg-brand-600 px-5 py-3 text-center text-base font-semibold text-white"
                    to="/login"
                    onClick={closeMobileNav}
                  >
                    {t("Menu.LogIn")}
                  </Link>
                </li>
              ) : (
                <li className="pb-2 pt-3">
                  <button
                    type="button"
                    className="getstarted block w-full rounded-full bg-brand-600 px-5 py-3 text-center text-base font-semibold text-white"
                    onClick={() => {
                      closeMobileNav();
                      onLogout();
                    }}
                  >
                    {t("Menu.Logout")}
                  </button>
                </li>
              )}
            </ul>
          </div>
        </div>
      )}
    </header>
  );
}

export default Menu;
